/**
 * Moon - Ultra-realistic lunar surface
 * Procedural textures with craters, maria, and highlands
 * Proper scale relative to Earth (1:4 ratio)
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface MoonProps {
  position: [number, number, number];
  radius?: number;
  earthPosition: [number, number, number];
}

export function Moon({
  position,
  radius = 0.3,
  earthPosition
}: MoonProps) {
  const moonRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.Mesh>(null);

  // Slow rotation - Moon is tidally locked but we add subtle rotation for visual interest
  useFrame((state) => {
    if (moonRef.current) {
      moonRef.current.rotation.y = state.clock.elapsedTime * 0.01;
    }
  });

  // Create ultra-detailed procedural Moon textures
  const { surfaceTexture, bumpTexture, roughnessTexture } = useMemo(() => {
    const size = 2048;

    // === SURFACE TEXTURE (Color Map) ===
    const surfaceCanvas = document.createElement('canvas');
    surfaceCanvas.width = size;
    surfaceCanvas.height = size / 2;
    const surfaceCtx = surfaceCanvas.getContext('2d')!;

    // Base lunar gray - natural silver tone
    surfaceCtx.fillStyle = '#a8a8a8';
    surfaceCtx.fillRect(0, 0, size, size / 2);

    // Create noise function for terrain variation
    const noise = (x: number, y: number, scale: number) => {
      const nx = Math.sin(x * scale) * Math.cos(y * scale * 0.7);
      const ny = Math.cos(x * scale * 1.3) * Math.sin(y * scale);
      return (nx + ny + 2) / 4;
    };

    // Maria (dark basalt plains) - major features
    const maria = [
      { cx: 0.3, cy: 0.45, rx: 0.15, ry: 0.12, name: 'Mare Imbrium' },
      { cx: 0.35, cy: 0.55, rx: 0.08, ry: 0.06, name: 'Mare Serenitatis' },
      { cx: 0.4, cy: 0.5, rx: 0.12, ry: 0.15, name: 'Mare Tranquillitatis' },
      { cx: 0.25, cy: 0.5, rx: 0.1, ry: 0.08, name: 'Oceanus Procellarum' },
      { cx: 0.5, cy: 0.45, rx: 0.06, ry: 0.05, name: 'Mare Crisium' },
      { cx: 0.45, cy: 0.6, rx: 0.05, ry: 0.04, name: 'Mare Fecunditatis' },
      { cx: 0.2, cy: 0.4, rx: 0.08, ry: 0.1, name: 'Mare Nubium' },
      { cx: 0.6, cy: 0.5, rx: 0.04, ry: 0.03, name: 'Mare Marginis' },
    ];

    // Draw maria (darker regions) - but still visible
    maria.forEach(mare => {
      const gradient = surfaceCtx.createRadialGradient(
        mare.cx * size, mare.cy * size / 2, 0,
        mare.cx * size, mare.cy * size / 2, mare.rx * size
      );
      gradient.addColorStop(0, 'rgba(100, 100, 110, 0.85)');
      gradient.addColorStop(0.5, 'rgba(115, 115, 120, 0.6)');
      gradient.addColorStop(1, 'rgba(140, 140, 145, 0)');

      surfaceCtx.fillStyle = gradient;
      surfaceCtx.beginPath();
      surfaceCtx.ellipse(
        mare.cx * size, mare.cy * size / 2,
        mare.rx * size, mare.ry * size / 2,
        0, 0, Math.PI * 2
      );
      surfaceCtx.fill();
    });

    // Highland regions (lighter areas)
    for (let i = 0; i < 35; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size / 2;
      const r = 40 + Math.random() * 100;

      const gradient = surfaceCtx.createRadialGradient(x, y, 0, x, y, r);
      gradient.addColorStop(0, 'rgba(190, 185, 180, 0.35)');
      gradient.addColorStop(0.5, 'rgba(170, 165, 160, 0.2)');
      gradient.addColorStop(1, 'rgba(150, 145, 140, 0)');

      surfaceCtx.fillStyle = gradient;
      surfaceCtx.beginPath();
      surfaceCtx.arc(x, y, r, 0, Math.PI * 2);
      surfaceCtx.fill();
    }

    // Large craters
    const largeCraters = [
      { x: 0.15, y: 0.3, r: 0.04, name: 'Tycho' },
      { x: 0.7, y: 0.25, r: 0.05, name: 'Copernicus' },
      { x: 0.8, y: 0.6, r: 0.03, name: 'Kepler' },
      { x: 0.55, y: 0.35, r: 0.025, name: 'Aristarchus' },
      { x: 0.25, y: 0.7, r: 0.035, name: 'Plato' },
    ];

    largeCraters.forEach(crater => {
      // Crater rim (natural brightness)
      const rimGradient = surfaceCtx.createRadialGradient(
        crater.x * size, crater.y * size / 2, crater.r * size * 0.7,
        crater.x * size, crater.y * size / 2, crater.r * size * 1.15
      );
      rimGradient.addColorStop(0, 'rgba(200, 195, 190, 0.7)');
      rimGradient.addColorStop(0.5, 'rgba(175, 170, 165, 0.45)');
      rimGradient.addColorStop(1, 'rgba(150, 145, 140, 0)');

      surfaceCtx.fillStyle = rimGradient;
      surfaceCtx.beginPath();
      surfaceCtx.arc(crater.x * size, crater.y * size / 2, crater.r * size * 1.15, 0, Math.PI * 2);
      surfaceCtx.fill();

      // Crater floor (shadowed)
      surfaceCtx.fillStyle = 'rgba(70, 70, 75, 0.5)';
      surfaceCtx.beginPath();
      surfaceCtx.arc(crater.x * size, crater.y * size / 2, crater.r * size * 0.6, 0, Math.PI * 2);
      surfaceCtx.fill();

      // Central peak (some craters)
      if (Math.random() > 0.5) {
        surfaceCtx.fillStyle = 'rgba(180, 175, 170, 0.7)';
        surfaceCtx.beginPath();
        surfaceCtx.arc(crater.x * size, crater.y * size / 2, crater.r * size * 0.15, 0, Math.PI * 2);
        surfaceCtx.fill();
      }

      // Ray system for young craters (like Tycho) - subtle rays
      if (crater.name === 'Tycho' || crater.name === 'Copernicus') {
        surfaceCtx.strokeStyle = 'rgba(190, 185, 180, 0.25)';
        surfaceCtx.lineWidth = 2;
        for (let ray = 0; ray < 14; ray++) {
          const angle = (ray / 14) * Math.PI * 2;
          const length = crater.r * size * (3.5 + Math.random() * 4);
          surfaceCtx.beginPath();
          surfaceCtx.moveTo(crater.x * size, crater.y * size / 2);
          surfaceCtx.lineTo(
            crater.x * size + Math.cos(angle) * length,
            crater.y * size / 2 + Math.sin(angle) * length * 0.5
          );
          surfaceCtx.stroke();
        }
      }
    });

    // Medium craters (hundreds)
    for (let i = 0; i < 400; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size / 2;
      const r = 3 + Math.random() * 12;

      // Crater with shadow and lit rim
      const shadowGradient = surfaceCtx.createLinearGradient(x - r, y, x + r, y);
      shadowGradient.addColorStop(0, 'rgba(60, 60, 65, 0.4)');
      shadowGradient.addColorStop(0.5, 'rgba(120, 115, 110, 0.25)');
      shadowGradient.addColorStop(1, 'rgba(170, 165, 160, 0.4)');

      surfaceCtx.fillStyle = shadowGradient;
      surfaceCtx.beginPath();
      surfaceCtx.arc(x, y, r, 0, Math.PI * 2);
      surfaceCtx.fill();
    }

    // Small craters (thousands - subtle detail)
    for (let i = 0; i < 2500; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size / 2;
      const r = 0.5 + Math.random() * 2;
      const brightness = 90 + Math.random() * 60;

      surfaceCtx.fillStyle = `rgba(${brightness}, ${brightness}, ${brightness + 3}, 0.5)`;
      surfaceCtx.beginPath();
      surfaceCtx.arc(x, y, r, 0, Math.PI * 2);
      surfaceCtx.fill();
    }

    // Add subtle color variation
    const imageData = surfaceCtx.getImageData(0, 0, size, size / 2);
    for (let i = 0; i < imageData.data.length; i += 4) {
      const variation = (Math.random() - 0.5) * 10;
      imageData.data[i] = Math.max(0, Math.min(255, imageData.data[i] + variation));
      imageData.data[i + 1] = Math.max(0, Math.min(255, imageData.data[i + 1] + variation));
      imageData.data[i + 2] = Math.max(0, Math.min(255, imageData.data[i + 2] + variation + 2)); // Slight blue tint
    }
    surfaceCtx.putImageData(imageData, 0, 0);

    const surfaceTex = new THREE.CanvasTexture(surfaceCanvas);
    surfaceTex.wrapS = THREE.RepeatWrapping;
    surfaceTex.wrapT = THREE.ClampToEdgeWrapping;

    // === BUMP MAP ===
    const bumpCanvas = document.createElement('canvas');
    bumpCanvas.width = size;
    bumpCanvas.height = size / 2;
    const bumpCtx = bumpCanvas.getContext('2d')!;

    // Base medium gray
    bumpCtx.fillStyle = '#808080';
    bumpCtx.fillRect(0, 0, size, size / 2);

    // Maria depressions (darker = lower)
    maria.forEach(mare => {
      const gradient = bumpCtx.createRadialGradient(
        mare.cx * size, mare.cy * size / 2, 0,
        mare.cx * size, mare.cy * size / 2, mare.rx * size
      );
      gradient.addColorStop(0, 'rgba(50, 50, 50, 0.8)');
      gradient.addColorStop(1, 'rgba(128, 128, 128, 0)');

      bumpCtx.fillStyle = gradient;
      bumpCtx.beginPath();
      bumpCtx.ellipse(mare.cx * size, mare.cy * size / 2, mare.rx * size, mare.ry * size / 2, 0, 0, Math.PI * 2);
      bumpCtx.fill();
    });

    // Crater bumps
    largeCraters.forEach(crater => {
      // Rim (high)
      bumpCtx.strokeStyle = 'rgba(200, 200, 200, 0.8)';
      bumpCtx.lineWidth = crater.r * size * 0.2;
      bumpCtx.beginPath();
      bumpCtx.arc(crater.x * size, crater.y * size / 2, crater.r * size * 0.9, 0, Math.PI * 2);
      bumpCtx.stroke();

      // Floor (low)
      bumpCtx.fillStyle = 'rgba(40, 40, 40, 0.7)';
      bumpCtx.beginPath();
      bumpCtx.arc(crater.x * size, crater.y * size / 2, crater.r * size * 0.6, 0, Math.PI * 2);
      bumpCtx.fill();
    });

    // Random small bumps
    for (let i = 0; i < 2000; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size / 2;
      const r = 1 + Math.random() * 8;
      const height = 100 + Math.random() * 100;

      bumpCtx.fillStyle = `rgba(${height}, ${height}, ${height}, 0.5)`;
      bumpCtx.beginPath();
      bumpCtx.arc(x, y, r, 0, Math.PI * 2);
      bumpCtx.fill();
    }

    const bumpTex = new THREE.CanvasTexture(bumpCanvas);
    bumpTex.wrapS = THREE.RepeatWrapping;
    bumpTex.wrapT = THREE.ClampToEdgeWrapping;

    // === ROUGHNESS MAP ===
    const roughCanvas = document.createElement('canvas');
    roughCanvas.width = 512;
    roughCanvas.height = 256;
    const roughCtx = roughCanvas.getContext('2d')!;

    // Moon is generally rough
    roughCtx.fillStyle = '#aaa';
    roughCtx.fillRect(0, 0, 512, 256);

    // Maria are smoother (lava plains)
    maria.forEach(mare => {
      roughCtx.fillStyle = 'rgba(80, 80, 80, 0.5)';
      roughCtx.beginPath();
      roughCtx.ellipse(mare.cx * 512, mare.cy * 256, mare.rx * 512, mare.ry * 256, 0, 0, Math.PI * 2);
      roughCtx.fill();
    });

    const roughTex = new THREE.CanvasTexture(roughCanvas);
    roughTex.wrapS = THREE.RepeatWrapping;
    roughTex.wrapT = THREE.ClampToEdgeWrapping;

    return {
      surfaceTexture: surfaceTex,
      bumpTexture: bumpTex,
      roughnessTexture: roughTex
    };
  }, []);

  // Calculate direction to Earth for subtle lighting effect
  const earthDir = useMemo(() => {
    const moonPos = new THREE.Vector3(...position);
    const earthPos = new THREE.Vector3(...earthPosition);
    return earthPos.clone().sub(moonPos).normalize();
  }, [position, earthPosition]);

  return (
    <group position={position}>
      {/* Main Moon sphere - Enhanced beauty */}
      <mesh ref={moonRef}>
        <sphereGeometry args={[radius, 128, 64]} />
        <meshStandardMaterial
          map={surfaceTexture}
          bumpMap={bumpTexture}
          bumpScale={0.025}  // More pronounced craters
          roughnessMap={roughnessTexture}
          roughness={0.75}   // Slightly smoother for better light catch
          metalness={0.05}   // Slight metallic sheen
          emissive={0x555566}
          emissiveIntensity={0.15}  // Brighter self-illumination
        />
      </mesh>

      {/* Strong directional light for dramatic shadows */}
      <directionalLight
        position={[radius * 8, radius * 4, radius * 6]}
        color={0xffffff}
        intensity={2.5}
        target-position={[0, 0, 0]}
      />

      {/* Soft fill light from Earth direction */}
      <pointLight
        position={[earthDir.x * radius * 3, earthDir.y * radius * 3, earthDir.z * radius * 3]}
        color={0x88aaff}  // Blue earthshine
        intensity={0.6}
        distance={radius * 6}
      />

      {/* Subtle earthshine rim - more visible */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[radius * 1.02, 64, 64]} />
        <meshBasicMaterial
          color={0xaabbdd}
          transparent
          opacity={0.12}
          blending={THREE.AdditiveBlending}
          side={THREE.BackSide}
          depthWrite={false}
        />
      </mesh>

      {/* Atmospheric glow */}
      <mesh>
        <sphereGeometry args={[radius * 1.08, 32, 32]} />
        <meshBasicMaterial
          color={0x7788aa}
          transparent
          opacity={0.06}
          blending={THREE.AdditiveBlending}
          side={THREE.BackSide}
          depthWrite={false}
        />
      </mesh>

      {/* Outer halo for dramatic effect */}
      <mesh>
        <sphereGeometry args={[radius * 1.15, 32, 32]} />
        <meshBasicMaterial
          color={0x556688}
          transparent
          opacity={0.03}
          blending={THREE.AdditiveBlending}
          side={THREE.BackSide}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}
